import { initializeApp, getApps, getApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import {
  getToken,
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from "firebase/app-check";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import {
  getMessaging,
  getToken as getFCMToken,
  onMessage,
  isSupported as isMessagingSupported,
} from "firebase/messaging";
import { getAnalytics } from "firebase/analytics";
import { logEvent } from "firebase/analytics";

let defaultAnalytics = null;

const PUSH_SETUP_ERROR_CODES = Object.freeze({
  UNSUPPORTED: "unsupported",
  MISSING_VAPID_KEY: "missing-vapid-key",
  PERMISSION_DENIED: "permission-denied",
  TOKEN_REQUEST_FAILED: "token-request-failed",
});

class PushSetupError extends Error {
  constructor(code, message, options = {}) {
    super(message);
    this.name = "PushSetupError";
    this.code = code;
    this.cause = options.cause;
  }
}

function setAppCheckDebugToken(debugToken) {
  if (typeof self === "undefined" || !debugToken) {
    return;
  }

  // App Check debug token must be boolean true (auto-generate) or a real token string.
  // eslint-disable-next-line no-restricted-globals
  self.FIREBASE_APPCHECK_DEBUG_TOKEN =
    debugToken === "true" ? true : debugToken;
}

function logAnalyticsEvent(name, params = {}) {
  const analytics = defaultAnalytics;

  if (!analytics || !name) {
    return false;
  }

  try {
    logEvent(analytics, name, params);
    return true;
  } catch {
    return false;
  }
}

function initializeEnterpriseFirebase({
  firebaseConfig,
  appName,
  appCheckSiteKey,
  appCheckDebugToken,
  fcmVapidKey,
  isDev = false,
  enableAnalytics = true,
} = {}) {
  if (!firebaseConfig) {
    throw new Error("initializeEnterpriseFirebase requires firebaseConfig.");
  }

  const shouldUseAppCheckDebug = Boolean(appCheckDebugToken);

  let app;
  if (appName) {
    try {
      app = getApp(appName);
    } catch {
      app = initializeApp(firebaseConfig, appName);
    }
  } else {
    app = getApps().length ? getApp() : initializeApp(firebaseConfig);
  }

  if (shouldUseAppCheckDebug) {
    setAppCheckDebugToken(appCheckDebugToken);
  }

  let appCheck;
  if (appCheckSiteKey) {
    appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(appCheckSiteKey),
      isTokenAutoRefreshEnabled: true,
    });
  } else if (isDev) {
    // eslint-disable-next-line no-console
    console.warn(
      "App Check not initialized: set a reCAPTCHA site key for local development."
    );
  }

  if (shouldUseAppCheckDebug && appCheck) {
    getToken(appCheck, true)
      .then(({ token }) => {
        // eslint-disable-next-line no-console
        console.info("App Check debug token ready:", token);
      })
      .catch(error => {
        // eslint-disable-next-line no-console
        console.error("App Check startup token fetch failed:", error);
      });
  }

  async function ensureAppCheckToken(forceRefresh = false) {
    if (!appCheck) {
      return null;
    }

    const { token } = await getToken(appCheck, forceRefresh);
    return token;
  }

  const auth = getAuth(app);
  const db = getFirestore(app);
  const storage = getStorage(app);

  if (typeof window !== "undefined") {
    window.db = db;
    window.storage = storage;
  }

  let analytics = null;
  if (enableAnalytics && typeof window !== "undefined") {
    try {
      analytics = getAnalytics(app);
      defaultAnalytics = analytics;
    } catch {
      analytics = null;
    }
  }

  async function requestFCMToken() {
    try {
      const supported = await isMessagingSupported();
      if (!supported) {
        throw new PushSetupError(
          PUSH_SETUP_ERROR_CODES.UNSUPPORTED,
          "Push messaging is not supported in this browser."
        );
      }
      if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
        throw new PushSetupError(
          PUSH_SETUP_ERROR_CODES.UNSUPPORTED,
          "Push messaging is not supported in this browser."
        );
      }
      const messaging = getMessaging(app);
      if (!fcmVapidKey) {
        // eslint-disable-next-line no-console
        console.warn(
          "FCM VAPID key not configured. Push notifications disabled."
        );
        throw new PushSetupError(
          PUSH_SETUP_ERROR_CODES.MISSING_VAPID_KEY,
          "FCM VAPID key not configured."
        );
      }
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        throw new PushSetupError(
          PUSH_SETUP_ERROR_CODES.PERMISSION_DENIED,
          "Browser notification permission was not granted."
        );
      }

      const serviceWorkerRegistration = await navigator.serviceWorker.register(
        "/firebase-messaging-sw.js"
      );

      if (!serviceWorkerRegistration.active) {
        await new Promise((resolve, reject) => {
          const worker =
            serviceWorkerRegistration.installing ||
            serviceWorkerRegistration.waiting;
          if (!worker) {
            resolve();
            return;
          }

          worker.addEventListener("statechange", () => {
            if (worker.state === "activated") {
              resolve();
            } else if (worker.state === "redundant") {
              reject(
                new Error("Service worker became redundant before activation.")
              );
            }
          });
        });
      }

      await navigator.serviceWorker.ready;

      return await getFCMToken(messaging, {
        vapidKey: fcmVapidKey,
        serviceWorkerRegistration,
      });
    } catch (err) {
      if (err instanceof PushSetupError) {
        throw err;
      }

      // eslint-disable-next-line no-console
      console.warn("FCM token request failed:", err);

      throw new PushSetupError(
        PUSH_SETUP_ERROR_CODES.TOKEN_REQUEST_FAILED,
        "Unable to create an FCM registration token.",
        { cause: err }
      );
    }
  }

  async function onFCMMessage(handler) {
    try {
      const supported = await isMessagingSupported();
      if (!supported) return () => {};
      const messaging = getMessaging(app);
      return onMessage(messaging, handler);
    } catch {
      return () => {};
    }
  }

  return {
    analytics,
    app,
    appCheck,
    auth,
    db,
    ensureAppCheckToken,
    logAnalyticsEvent,
    onFCMMessage,
    requestFCMToken,
    storage,
  };
}

export {
  initializeEnterpriseFirebase,
  logAnalyticsEvent,
  PUSH_SETUP_ERROR_CODES,
  PushSetupError,
};
